import classNames from 'classnames'
import React from 'react'

import type { EmptyStateLauncherProps } from 'shared-libs/components/ui2/fields/field-ui/empty-state-launcher/empty-state-launcher-props'
import { useFieldShellContext } from 'shared-libs/components/ui2/fields/field-ui/field-shell/field-shell-context'

import './_empty-state-launcher.scss'

type WebEmptyStateLauncherProps = Omit<EmptyStateLauncherProps, 'translationTable' | 'iconName'> & {
  /** Pass a pre-rendered icon element (e.g. a Lucide SVG from the entity-display registry). */
  icon?: React.ReactNode
  className?: string
  'aria-label'?: string
}

/**
 * Empty-state launcher button for DW/MW.
 * Callers are responsible for pre-translating `message` before passing it in.
 * Icon rendering is caller-owned (pass a rendered element via `icon`).
 *
 * Disabled and error treatment come from `FieldShellContext` unless the
 * caller overrides them, so a launcher inside an errored field turns red
 * without the field wiring anything up.
 */
export function UI2EmptyStateLauncher(props: WebEmptyStateLauncherProps) {
  const { onPress, message, icon, className, ...rest } = props
  const shell = useFieldShellContext()
  const isDisabled = props.isDisabled ?? shell.isDisabled
  const hasError = props.hasError ?? shell.hasError
  return (
    <button
      type='button'
      className={classNames(
        'ui2-emptyStateLauncher',
        {
          'ui2-emptyStateLauncher--disabled': isDisabled,
          'ui2-emptyStateLauncher--error': hasError && !isDisabled,
        },
        className,
      )}
      onClick={!isDisabled ? onPress : undefined}
      disabled={isDisabled}
      aria-label={rest['aria-label'] ?? message}
    >
      {icon ? <span className='ui2-emptyStateLauncher-icon'>{icon}</span> : null}
      <span className='ui2-emptyStateLauncher-text'>{message}</span>
    </button>
  )
}
